import React, { useEffect, useState } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { post, postApi } from "../../api/getUser";
import { NATEXT, s3Url } from "../constants";
import {
  colorBg,
  colorBorder,
  colorGreen,
  //colorLightGreen,
} from "../../utils/palette";
import { ActionIconContainer, CustomLoader } from "./TableElements";
import defaultVehicleIcon from "../img/defaultVehicleIcon.png";
import { getTranslatedFuelTypeUnit } from "../helpers";
import { getVehicleBorderColor, isVehicleInactive } from "../../utils/helpers";
import { VehicleStatus } from "../../components/Assets/Vehicles/VehiclesTable";

const VehicleDetailsPopup = ({ showPopup, setShowPopup, vehicleId }) => {
  const { t } = useTranslation("common");
  const { orgId } = useSelector((state) => state.user);
  const groupsList = useSelector((state) => state.groups.list);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const vehicleImage = vehicleDetails?.veh_img
    ? `${s3Url}/org_${orgId}/vehicle_${vehicleDetails.veh_id}/${vehicleDetails.veh_img}`
    : defaultVehicleIcon;
  const vehicleBorderColor = getVehicleBorderColor(
    vehicleDetails?.veh_active,
    vehicleDetails?.secondary_status,
  );
  const isInactiveVehicle = isVehicleInactive(vehicleDetails?.veh_active);
  const isEquipment = vehicleDetails?.asset_type === 1;

  useEffect(() => {
    if (showPopup && vehicleId) {
      fetchVehicleDetails();
    } else {
      setVehicleDetails(null);
    }
  }, [showPopup, vehicleId]);

  const fetchVehicleDetails = async () => {
    setLoading(true);
    const res = await post("vehicle", {
      veh_id: vehicleId,
      operation: "fetch_single_vehicle_details",
    });

    if (res?.success && res?.data?.length > 0) {
      const vehicleData = res.data[0];
      setVehicleDetails(vehicleData);
    }
    setLoading(false);
  };

  const getGroupName = (groupId) => {
    if (!groupId) return "";
    const group = groupsList.find((g) => g.group_id === groupId);
    return group ? group.group_name : "";
  };

  const getStatusColor = (status) => {
    return status === 1 ? colorGreen : "gray";
  };

  const getStatusBg = (status) => {
    return status === 1 ? colorGreen : "#f0f0f0";
  };

  const renderValue = (val) => val || NATEXT;

  return (
    <Modal
      show={showPopup}
      onHide={() => setShowPopup(false)}
      centered
      size="md"
    >
      <Modal.Header style={{ borderBottom: "none", paddingBottom: 0 }}>
        <Modal.Title style={{ fontSize: "1.1rem", fontWeight: 500 }}>
          {isEquipment ? t("equipment_details") : t("vehicle_details")}
        </Modal.Title>
        <div className="d-flex justify-content-end">
          <ActionIconContainer onClick={() => setShowPopup(false)}>
            <i className="fi fi-rr-cross-small" />
          </ActionIconContainer>
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center">
            <CustomLoader />
          </div>
        ) : vehicleDetails ? (
          <div>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center">
              {/* Image and name section */}
              <div className="d-flex gap-2 align-items-center">
                {/* Image */}
                <img
                  src={vehicleImage}
                  alt="vehicle-img"
                  style={{
                    width: "40px",
                    height: "40px",
                    objectFit: "cover",
                    border: `1px solid ${vehicleBorderColor}`,
                    background: "#D4E5F6",
                    filter: isInactiveVehicle ? "grayscale(100%)" : "none",
                    padding: "2px",
                  }}
                  className="rounded-circle"
                />

                {/* Name and details */}
                <div className="d-flex flex-column justify-content-center">
                  <h5 style={{ fontWeight: 500 }}>{vehicleDetails.name}</h5>
                  {vehicleDetails?.group_name && (
                    <span>{vehicleDetails.group_name}</span>
                  )}
                  {vehicleDetails?.secondary_status == 1 &&
                    vehicleDetails?.veh_active == 1 && (
                      <p style={{ fontSize: "12px", color: "red" }}>
                        {t("out_of_service")}
                      </p>
                    )}
                </div>
              </div>

              {/* Vehicle Status */}
              <VehicleStatus status={vehicleDetails?.veh_active} />
            </div>

            <hr />
            {/* Details Grid */}
            <Row className="g-3">
              <Col xs={6}>
                <DetailItem>
                  <label>{t("type")}</label>
                  <p>{renderValue(vehicleDetails.veh_type)}</p>
                </DetailItem>
              </Col>
              <Col xs={6}>
                <DetailItem>
                  <label>{t("license")}</label>
                  <p>{renderValue(vehicleDetails.license_no)}</p>
                </DetailItem>
              </Col>
              <Col xs={6}>
                <DetailItem>
                  <label>{t("make_and_model")}</label>
                  <p>
                    {renderValue(vehicleDetails.make)}{" "}
                    {renderValue(vehicleDetails.model)}
                  </p>
                </DetailItem>
              </Col>
              <Col xs={6}>
                <DetailItem>
                  <label>{t("year")}</label>
                  <p>{renderValue(vehicleDetails.year)}</p>
                </DetailItem>
              </Col>
              <Col xs={12}>
                <DetailItem>
                  <label>{t("vin")}</label>
                  <p>{renderValue(vehicleDetails.vin_no)}</p>
                </DetailItem>
              </Col>
            </Row>
          </div>
        ) : (
          <div className="text-center py-3">{t("no_data_found")}</div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default VehicleDetailsPopup;

// Styles
const DetailItem = styled.div`
  label {
    display: block;
    font-size: 12px;
    color: black;
    margin-bottom: 2px;
  }
  p {
    //font-size: 0.95rem;
    //font-weight: 500;
    margin-bottom: 0;
    color: black;
  }
`;
